import { useEffect, useState } from 'react';
import { Plus, Search, Edit, Trash2, X, User, DollarSign, Calendar, CheckCircle, XCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';

interface Credit {
  id: string;
  driver_name: string;
  amount: number;
  reason: string;
  date: string;
  status: 'pending' | 'used';
  created_at: string;
}

interface Driver {
  id: string;
  first_name: string;
  last_name: string;
}

export function CreditsView() {
  const toast = useToast();
  const [credits, setCredits] = useState<Credit[]>([]);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'pending' | 'used' | 'all'>('pending');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCredit, setSelectedCredit] = useState<Credit | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [creditToDelete, setCreditToDelete] = useState<Credit | null>(null);
  const [driverSearchTerm, setDriverSearchTerm] = useState('');
  const [showDriverDropdown, setShowDriverDropdown] = useState(false);

  const [formData, setFormData] = useState({
    driver_name: '',
    amount: '',
    reason: '',
    date: new Date().toISOString().split('T')[0],
    status: 'pending' as 'pending' | 'used',
  });

  useEffect(() => {
    loadCredits();
    loadDrivers();
  }, []);

  async function loadDrivers() {
    try {
      const { data, error } = await supabase
        .from('drivers')
        .select('id, first_name, last_name, driver_status')
        .order('last_name');

      if (error) throw error;

      // Filter to only show active drivers (including those with NULL status)
      const activeDrivers = (data || []).filter(d => 
        d.driver_status === 'active' || d.driver_status === null || d.driver_status === undefined
      );
      
      setDrivers(activeDrivers);
    } catch (error) {
      console.error('Error loading drivers:', error);
      toast.error('Failed to load drivers');
    }
  }

  async function loadCredits() {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('driver_credits')
        .select('*')
        .order('date', { ascending: false });

      if (error) throw error;

      setCredits(data || []);
    } catch (error) {
      console.error('Error loading credits:', error);
      toast.error('Failed to load credits');
    } finally {
      setLoading(false);
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.driver_name || !formData.amount || !formData.reason) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const { error } = await supabase
        .from('driver_credits')
        .insert([{
          driver_name: formData.driver_name,
          amount: parseFloat(formData.amount),
          reason: formData.reason,
          date: formData.date,
          status: formData.status,
        }]);

      if (error) throw error;

      toast.success('Credit added successfully!');
      setShowAddModal(false);
      resetForm();
      loadCredits();
    } catch (error) {
      console.error('Error adding credit:', error);
      toast.error('Failed to add credit');
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedCredit || !formData.driver_name || !formData.amount || !formData.reason) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const { error } = await supabase
        .from('driver_credits')
        .update({
          driver_name: formData.driver_name,
          amount: parseFloat(formData.amount),
          reason: formData.reason,
          date: formData.date,
          status: formData.status,
        })
        .eq('id', selectedCredit.id);

      if (error) throw error;

      toast.success('Credit updated successfully!');
      setShowEditModal(false);
      setSelectedCredit(null);
      resetForm();
      loadCredits();
    } catch (error) {
      console.error('Error updating credit:', error);
      toast.error('Failed to update credit');
    }
  };

  const handleDelete = async () => {
    if (!creditToDelete) return;

    try {
      const { error } = await supabase
        .from('driver_credits')
        .delete()
        .eq('id', creditToDelete.id);

      if (error) throw error;

      toast.success('Credit deleted successfully!');
      setShowDeleteConfirm(false);
      setCreditToDelete(null);
      loadCredits();
    } catch (error) {
      console.error('Error deleting credit:', error);
      toast.error('Failed to delete credit');
    }
  };

  const openEditModal = (credit: Credit) => {
    setSelectedCredit(credit);
    setFormData({
      driver_name: credit.driver_name,
      amount: credit.amount.toString(),
      reason: credit.reason,
      date: credit.date,
      status: credit.status,
    });
    setDriverSearchTerm(credit.driver_name);
    setShowDriverDropdown(false);
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      driver_name: '',
      amount: '',
      reason: '',
      date: new Date().toISOString().split('T')[0],
      status: 'pending',
    });
  };

  const filteredCredits = credits.filter(credit => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = (
      credit.driver_name.toLowerCase().includes(searchLower) ||
      credit.reason.toLowerCase().includes(searchLower)
    );
    
    const matchesStatus = 
      statusFilter === 'all' ||
      (statusFilter === 'pending' && credit.status === 'pending') ||
      (statusFilter === 'used' && credit.status === 'used');
    
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="flex-none border-b border-gray-200 bg-white px-6 py-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Driver Credits</h1>
            <p className="text-sm text-gray-500 mt-1">Manage credits to be paid to drivers</p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Add Credit
          </button>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by driver name or reason..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>
          
          {/* Status Filter */}
          <div className="flex gap-2">
            <button
              onClick={() => setStatusFilter('pending')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                statusFilter === 'pending'
                  ? 'bg-yellow-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Not Used
            </button>
            <button
              onClick={() => setStatusFilter('used')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                statusFilter === 'used'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Used
            </button>
            <button
              onClick={() => setStatusFilter('all')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                statusFilter === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All
            </button>
          </div>
        </div>
      </div>

      {/* Credits Table */}
      <div className="flex-1 overflow-auto">
        <table className="w-full">
          <thead className="bg-gray-50 sticky top-0">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Driver</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reason</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredCredits.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                  <DollarSign className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                  <p className="text-lg font-medium">No credits found</p>
                  <p className="text-sm mt-1">Add your first credit to get started</p>
                </td>
              </tr>
            ) : (
              filteredCredits.map((credit) => (
                <tr key={credit.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <User className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="text-sm font-medium text-gray-900">{credit.driver_name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-600">{new Date(credit.date).toLocaleDateString()}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">{credit.reason}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm font-semibold text-green-600">${credit.amount.toFixed(2)}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={credit.status === 'used'}
                        onChange={async () => {
                          try {
                            const newStatus = credit.status === 'pending' ? 'used' : 'pending';
                            const { error } = await supabase
                              .from('driver_credits')
                              .update({ status: newStatus })
                              .eq('id', credit.id);
                            
                            if (error) throw error;
                            toast.success(`Credit marked as ${newStatus}`);
                            loadCredits();
                          } catch (error) {
                            console.error('Error updating status:', error);
                            toast.error('Failed to update status');
                          }
                        }}
                        className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500 cursor-pointer"
                      />
                      {credit.status === 'used' ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <XCircle className="w-5 h-5 text-yellow-600" />
                      )}
                      <span className={`text-sm font-medium ${
                        credit.status === 'used' ? 'text-green-600' : 'text-yellow-600'
                      }`}>
                        {credit.status === 'used' ? 'Used' : 'Not Used'}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => openEditModal(credit)}
                      className="text-blue-600 hover:text-blue-900 mr-3"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => {
                        setCreditToDelete(credit);
                        setShowDeleteConfirm(true);
                      }}
                      className="text-red-600 hover:text-red-900"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Add Credit</h2>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  resetForm();
                  setDriverSearchTerm('');
                  setShowDriverDropdown(false);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              <div className="space-y-4">
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Driver Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={driverSearchTerm || formData.driver_name}
                    onChange={(e) => {
                      setDriverSearchTerm(e.target.value);
                      setShowDriverDropdown(true);
                      if (!e.target.value) {
                        setFormData({ ...formData, driver_name: '' });
                      }
                    }}
                    onFocus={() => setShowDriverDropdown(true)}
                    placeholder="Search for a driver..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    required
                  />
                  {showDriverDropdown && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {drivers
                        .filter(driver => {
                          const fullName = `${driver.first_name} ${driver.last_name}`.toLowerCase();
                          return fullName.includes(driverSearchTerm.toLowerCase());
                        })
                        .map(driver => (
                          <button
                            key={driver.id}
                            type="button"
                            onClick={() => {
                              const fullName = `${driver.first_name} ${driver.last_name}`;
                              setFormData({ ...formData, driver_name: fullName });
                              setDriverSearchTerm(fullName);
                              setShowDriverDropdown(false);
                            }}
                            className="w-full px-3 py-2 text-left hover:bg-gray-100 transition-colors"
                          >
                            <span className="text-sm text-gray-900">{driver.first_name} {driver.last_name}</span>
                          </button>
                        ))}
                      {drivers.filter(driver => {
                        const fullName = `${driver.first_name} ${driver.last_name}`.toLowerCase();
                        return fullName.includes(driverSearchTerm.toLowerCase());
                      }).length === 0 && (
                        <div className="px-3 py-2 text-sm text-gray-500">
                          No drivers found
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reason <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.reason}
                    onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                    placeholder="e.g., Bonus, Referral, Adjustment"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Amount <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    placeholder="0.00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as 'pending' | 'used' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    required
                  >
                    <option value="pending">Pending</option>
                    <option value="used">Used</option>
                  </select>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                    setDriverSearchTerm('');
                    setShowDriverDropdown(false);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  Add Credit
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedCredit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Edit Credit</h2>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedCredit(null);
                  resetForm();
                  setDriverSearchTerm('');
                  setShowDriverDropdown(false);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleUpdate} className="p-6">
              <div className="space-y-4">
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Driver Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={driverSearchTerm || formData.driver_name}
                    onChange={(e) => {
                      setDriverSearchTerm(e.target.value);
                      setShowDriverDropdown(true);
                      if (!e.target.value) {
                        setFormData({ ...formData, driver_name: '' });
                      }
                    }}
                    onFocus={() => setShowDriverDropdown(true)}
                    placeholder="Search for a driver..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    required
                  />
                  {showDriverDropdown && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {drivers
                        .filter(driver => {
                          const fullName = `${driver.first_name} ${driver.last_name}`.toLowerCase();
                          return fullName.includes(driverSearchTerm.toLowerCase());
                        })
                        .map(driver => (
                          <button
                            key={driver.id}
                            type="button"
                            onClick={() => {
                              const fullName = `${driver.first_name} ${driver.last_name}`;
                              setFormData({ ...formData, driver_name: fullName });
                              setDriverSearchTerm(fullName);
                              setShowDriverDropdown(false);
                            }}
                            className="w-full px-3 py-2 text-left hover:bg-gray-100 transition-colors"
                          >
                            <span className="text-sm text-gray-900">{driver.first_name} {driver.last_name}</span>
                          </button>
                        ))}
                      {drivers.filter(driver => {
                        const fullName = `${driver.first_name} ${driver.last_name}`.toLowerCase();
                        return fullName.includes(driverSearchTerm.toLowerCase());
                      }).length === 0 && (
                        <div className="px-3 py-2 text-sm text-gray-500">
                          No drivers found
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reason <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.reason}
                    onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                    placeholder="e.g., Bonus, Referral, Adjustment"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Amount <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    placeholder="0.00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as 'pending' | 'used' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    required
                  >
                    <option value="pending">Pending</option>
                    <option value="used">Used</option>
                  </select>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedCredit(null);
                    resetForm();
                    setDriverSearchTerm('');
                    setShowDriverDropdown(false);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  Update Credit
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && creditToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Credit</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this credit for {creditToDelete.driver_name}? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setCreditToDelete(null);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
